import type { IEdgeRegistrationFormDto } from '@experiences/interfaces';
import {
    useNavigateWithParams,
    useRouteResolver,
} from '@experiences/util';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { routePaths } from '../../common/constants/routePaths';
import { registerEdge } from '../../services/frp/EdgeRegistrationService';
import { accountGlobalId } from '../../store/selectors';

const DEFAULT_VALUES = {
    customDomain: '',
    cidr: '',
    kubeconfigFile: null as unknown as File | FileList,
};

const useRegisterEdgeViewModel = () => {
    const { formatMessage: translate } = useIntl();
    const navigate = useNavigateWithParams();
    const getRoute = useRouteResolver();
    const { tenantId } = useParams() as { tenantId: string };
    const accountId = useSelector(accountGlobalId);

    const {
        control, handleSubmit, formState, watch, setValue, setError, clearErrors,
    } = useForm<IEdgeRegistrationFormDto>({
        mode: 'onSubmit',
        defaultValues: { ...DEFAULT_VALUES },
    });

    const onSubmit = useCallback(async (data: IEdgeRegistrationFormDto) => {
        if (
            !data.kubeconfigFile ||
            (data.kubeconfigFile instanceof FileList && data.kubeconfigFile.length === 0) ||
            (data.kubeconfigFile instanceof File && data.kubeconfigFile.size === 0)
        ) {
            setError('kubeconfigFile', {
                type: 'required',
                message: translate(
                    { id: 'CLIENT_REQUIRED_KUBECONFIG_FIELD_ERROR' },
                ),
            });
            return;
        }

        await registerEdge(accountId, tenantId, data);
        navigate(getRoute(routePaths.adminTenantEdge.replace(':tenantId', tenantId)));

    }, [ accountId, tenantId, getRoute, navigate, setError, translate ]);

    return {
        methods: {
            watch,
            formState,
            handleSubmit: handleSubmit(onSubmit),
            setValue,
            clearErrors,
            control,
        },
        onSubmit,
    };
};

export default useRegisterEdgeViewModel;
